import React from 'react';
import { motion } from 'framer-motion';
import { Clock, Compass, ListChecks, Volume2 } from 'lucide-react';
const finderFilters = [{
  label: 'Level',
  value: 'Beginner · Intermediate · Advanced'
}, {
  label: 'Focus',
  value: 'Technique · Footwork · Defense · Counters · Combinations · Conditioning · Tactics'
}, {
  label: 'Session type',
  value: 'Individual · Partner · Pads · Bag · Tactical · Conditioning'
}, {
  label: 'Duration',
  value: '30 · 45 · 60 · 75+ minutes'
}, {
  label: 'Training need',
  value: 'Foundation · Correction · Progression · Reaction · Pressure · Assessment'
}, {
  label: 'Context',
  value: 'Individual · Small group · Large group · Limited equipment · No-prep'
}];
const sessionAnatomy = ['Clear objective', 'Recommended athlete level', 'Equipment', 'Structured warm-up', 'Technical preparation', 'Main drills', 'Exact rounds or work/rest', 'Coaching cues', 'Common errors', 'Corrections', 'Regressions', 'Progressions', 'Conditioning or finisher when appropriate', 'Cool-down', 'Success criteria', 'Safety considerations', 'Exit standard where appropriate'];
const supportPillars = [{
  icon: Volume2,
  title: 'Coaching cues & corrections',
  text: 'Short, practical cues designed to correct one meaningful cause at a time — usable while the athlete is standing in front of you.'
}, {
  icon: ListChecks,
  title: 'Progressions & regressions',
  text: 'Know when to repeat, simplify, slow down, reduce range, remove a variable, add movement, reaction or decision-making, or progress toward tactics.'
}];
export function TeachingLibrary() {
  return <section className="py-24 px-4 bg-section border-y border-line" data-id="element-291" data-mp="3j6kil6foc9hu">
      <div className="max-w-6xl mx-auto" data-id="element-292" data-mp="3j7vll41apd7w">
        <div className="text-center mb-16" data-id="element-293" data-mp="2lrorb34gkblq">
          <p className="text-gold font-semibold tracking-widest uppercase text-xs mb-3" data-id="element-294" data-mp="2lqnd4ps5b8zs">
            The core of Boxing Coach Ready
          </p>
          <h2 className="text-4xl md:text-5xl font-display font-bold mb-4 text-white" data-id="element-295" data-mp="2ipbw15ix3657">
            <span className="text-gold" data-id="element-296" data-mp="2enyq6vftr2ya">150 complete</span> ready-to-coach sessions
          </h2>
          <p className="text-xl text-mist" data-id="element-297" data-mp="2sfdla0n0aulp">
            Organized by skill, level and coaching objective
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8 mb-8" data-id="element-298" data-mp="2ng0ooo6c295x">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.3,
          ease: [0.23, 1, 0.32, 1]
        }} className="lg:col-span-3 bg-card border border-line rounded-2xl p-8" data-id="element-299" data-mp="2nimuai9nyeli">
            <div className="flex items-center gap-3 mb-2" data-id="element-300" data-mp="24psrsnt88ey6">
              <Compass className="text-gold" size={22} aria-hidden="true" data-id="element-301" data-mp="24o7x8y6www0b" />
              <h3 className="text-2xl font-display font-bold text-white" data-id="element-302" data-mp="3l2zq1c7z05a5">Session Finder</h3>
            </div>
            <p className="text-mist mb-6" data-id="element-303" data-mp="3g469faxdzy5r">
              Instead of asking “What should I coach today?”, narrow the material to what your
              athletes actually need.
            </p>
            <dl className="space-y-3" data-id="element-304" data-mp="16xcnj15wrjiy">
              {finderFilters.map(filter => <div key={filter.label} className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-4 border-b border-line pb-3 last:border-0 last:pb-0" data-id="element-305" data-mp="sxa2x767uc00">
                  <dt className="text-sm font-display font-semibold text-gold uppercase tracking-wider sm:w-32 shrink-0" data-id="element-306" data-mp="1lmh30albj9nd">
                    {filter.label}
                  </dt>
                  <dd className="text-sm text-mist" data-id="element-307" data-mp="1bw5yomxq2yfy">{filter.value}</dd>
                </div>)}
            </dl>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.3,
          delay: 0.05,
          ease: [0.23, 1, 0.32, 1]
        }} className="lg:col-span-2 bg-canvas border border-gold/40 rounded-2xl p-8" data-id="element-308" data-mp="1gsctpkokypj4">
            <div className="flex items-center gap-3 mb-4" data-id="element-309" data-mp="fq4bvtdmb26f">
              <Clock className="text-gold" size={22} aria-hidden="true" data-id="element-310" data-mp="fojgk1jhbf7i" />
              <h3 className="text-xl font-display font-bold text-white" data-id="element-311" data-mp="bp28r1gky5z6">Session durations</h3>
            </div>
            <p className="text-sm text-mist mb-5" data-id="element-312" data-mp="6qqy4oignrfw">
              From a short technical block to a complete training session.
            </p>
            <ul className="grid grid-cols-2 gap-2 text-sm" data-id="element-313" data-mp="zdtwngn50265">
              {['30 min', '45 min', '60 min', '75+ min'].map(duration => <li key={duration} className="border border-gold/30 rounded-lg px-3 py-3 text-center font-display font-semibold text-white text-lg" data-id="element-314" data-mp="ultz1uujhlcq">
                  {duration}
                </li>)}
            </ul>
          </motion.div>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.3,
        ease: [0.23, 1, 0.32, 1]
      }} className="bg-card border border-line rounded-2xl p-8 mb-8" data-id="element-315" data-mp="1ibeisfyiomn2">
          <h3 className="text-xl font-display font-bold text-white mb-6" data-id="element-316" data-mp="1zkgsuf5mng1l">Every session includes</h3>
          <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3" data-id="element-317" data-mp="23p6mrygq6kjv">
            {sessionAnatomy.map(item => <li key={item} className="flex items-start gap-3" data-id="element-318" data-mp="28kvreg7lqrn1">
                <span className="mt-2 w-1.5 h-1.5 rounded-full bg-gold shrink-0" aria-hidden="true" data-id="element-319" data-mp="2dki2ok3vdxt7" />
                <span className="text-sm text-mist" data-id="element-320" data-mp="2id1htli7h104">{item}</span>
              </li>)}
          </ul>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6" data-id="element-321" data-mp="2ndfgicxx2ylh">
          {supportPillars.map(pillar => <div key={pillar.title} className="bg-card border border-line rounded-2xl p-6" data-id="element-322" data-mp="2rbd3ra84nam8">
              <pillar.icon className="text-gold mb-4" size={22} aria-hidden="true" data-id="element-323" data-mp="2wa7ztdv3uvoa" />
              <h4 className="font-display font-bold text-lg text-white mb-2" data-id="element-324" data-mp="wab3hr6wahcl">{pillar.title}</h4>
              <p className="text-sm text-mist leading-relaxed" data-id="element-325" data-mp="saap26jsq65j">{pillar.text}</p>
            </div>)}
        </div>
      </div>
    </section>;
}